import type * as Api from '@ViewModels';
import type { Edge, Node } from '@xyflow/react';
import type { BaseNodeData } from '../types';

interface ReconcileOptimisticStepNodeParams {
	optimisticNodeId: string;
	createdStep: Api.IAutomationStep;
	automationTemplate?: Api.IAutomationTemplate;
	setNodes: React.Dispatch<React.SetStateAction<Node<BaseNodeData>[]>>;
	setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
}

/**
 * Reconciles an optimistic step node with the canonical server-backed node ID.
 *
 * We create optimistic step nodes with temporary IDs, but server-expanded flow
 * uses `step-{stepId}`. Rewriting the optimistic node ID early avoids a
 * structural ID swap later (which can present as duplicate/flickering nodes).
 */
export function reconcileOptimisticStepNode({
	optimisticNodeId,
	createdStep,
	automationTemplate,
	setNodes,
	setEdges,
}: ReconcileOptimisticStepNodeParams): void {
	const canonicalNodeId = `step-${createdStep.id}`;

	setNodes(currentNodes =>
		currentNodes.map(node => {
			if (node.id !== optimisticNodeId) {
				return node;
			}

			return {
				...node,
				id: canonicalNodeId,
				data: {
					...node.data,
					nodeId: canonicalNodeId,
					stepId: createdStep.id,
					automationStep: createdStep,
					automationTemplate,
					stepData: {
						...(node.data as { stepData?: Record<string, unknown> }).stepData,
						name: createdStep.name,
					},
				},
			};
		})
	);

	setEdges(currentEdges =>
		currentEdges.map(edge => {
			const source = edge.source === optimisticNodeId ? canonicalNodeId : edge.source;
			const target = edge.target === optimisticNodeId ? canonicalNodeId : edge.target;

			if (source === edge.source && target === edge.target) {
				return edge;
			}

			return {
				...edge,
				source,
				target,
			};
		})
	);
}
